import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Alert, Spin, Card, Tag, Badge, Typography, Space, Dropdown, Menu, message, List } from 'antd';
import { 
  EditOutlined, 
  SaveOutlined, 
  CloseOutlined, 
  PlusOutlined, 
  DeleteOutlined,
  CalendarOutlined,
  BookOutlined,
  CheckCircleOutlined,
  LockOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import TimetableEntry from './TimetableEntry';
import moment from 'moment';
import api from '../../../../api';

const { Text, Title } = Typography;

const DailyOverrideModal = ({
  visible,
  onCancel,
  onSave,
  selectedDate,
  classId,
  academicYearId,
  semesterType,
  termId,
  termName,
  termNumber,
  isSemesterBased,
  tenantId,
  subjects,
  labBatches,
  token,
  facultyData = [],
  electiveGroups = [],
  loadingElectives = false,
  organizationId,
  className,
  academicYearName,
  createdBy,
  createdByName,
  academicYearStartDate,
  academicYearEndDate
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [overriddenEntries, setOverriddenEntries] = useState([]);
  const [dayName, setDayName] = useState('');
  const [existingOverride, setExistingOverride] = useState(null);
  const [error, setError] = useState('');
  const [conflicts, setConflicts] = useState([]);
  const [newEntryIndex, setNewEntryIndex] = useState(null);
  const [isDateLocked, setIsDateLocked] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const entriesEndRef = useRef(null);

  // ==================== Helper Functions ====================
  
  const facultyMap = useCallback(() => {
    const map = {};
    (facultyData || []).forEach(faculty => {
      const facultyId = faculty.userId || faculty._id || faculty.staffId || faculty.id;
      if (facultyId) {
        map[facultyId] = {
          id: facultyId,
          name: faculty.name || `${faculty.firstName || ''} ${faculty.lastName || ''}`.trim(),
          email: faculty.email || ''
        };
      }
    });
    return map;
  }, [facultyData]);

  const formattedLabBatches = useCallback(() => {
    const formatted = (labBatches || []).map(batch => {
      if (batch.name && (batch.name.toLowerCase().includes('batch'))) {
        return {
          ...batch,
          batchName: batch.name
        };
      }
      const batchNumber = batch.batchNumber || batch.batchId || '1';
      return {
        ...batch,
        name: `Batch ${batchNumber}`,
        batchName: `Batch ${batchNumber}`
      };
    });
    return formatted;
  }, [labBatches]);

  const enhanceEntryWithFacultyAndBatchData = useCallback((entry) => {
    const facultyMapData = facultyMap();
    const labBatchesFormatted = formattedLabBatches();
    
    const enhancedEntry = { ...entry };
    
    if (entry.facultyUserId) {
      const facultyInfo = facultyMapData[entry.facultyUserId];
      if (facultyInfo) {
        enhancedEntry.facultyName = facultyInfo.name;
        enhancedEntry.facultyEmail = facultyInfo.email;
      }
    }

    if (!enhancedEntry.facultyUserId && Array.isArray(entry.allocations) && entry.allocations.length > 0) {
      const firstAllocFaculty = entry.allocations[0]?.facultyUserId;
      if (firstAllocFaculty) {
        enhancedEntry.facultyUserId = firstAllocFaculty;
        const facultyInfo = facultyMapData[firstAllocFaculty];
        if (facultyInfo) enhancedEntry.facultyName = facultyInfo.name;
      }
    }
    
    if (entry.batchId) {
      const batchInfo = labBatchesFormatted.find(batch => 
        String(batch._id) === String(entry.batchId) || 
        String(batch.id) === String(entry.batchId)
      );
      if (batchInfo) {
        enhancedEntry.batchName = batchInfo.name || batchInfo.batchName;
      }
    }

    if (!enhancedEntry.batchId && Array.isArray(entry.allocations) && entry.allocations.length > 0) {
      const firstAllocBatch = entry.allocations[0]?.batchId;
      if (firstAllocBatch) {
        enhancedEntry.batchId = firstAllocBatch;
        const batchInfo = labBatchesFormatted.find(batch =>
          String(batch._id) === String(firstAllocBatch) || String(batch.id) === String(firstAllocBatch)
        );
        if (batchInfo) enhancedEntry.batchName = batchInfo.name || batchInfo.batchName;
      }
    }
    
    if (Array.isArray(entry.allocations)) {
      enhancedEntry.allocations = entry.allocations.map(allocation => {
        const enhancedAllocation = { ...allocation };
        if (allocation.facultyUserId) {
          const allocFacultyInfo = facultyMapData[allocation.facultyUserId];
          if (allocFacultyInfo) {
            enhancedAllocation.facultyName = allocFacultyInfo.name;
          }
        }
        return enhancedAllocation;
      });
    }
    
    return enhancedEntry;
  }, [facultyMap, formattedLabBatches]);

  const buildElectiveSelections = useCallback((groupId, selectedSubjectIds = [], selectedSubgroupIds = []) => {
    if (!groupId) return [];
    const group = (electiveGroups || []).find(g => String(g._id) === String(groupId));
    if (!group) return [];

    const electiveSelections = [];
    (group.subjects || []).forEach(groupSubject => {
      const subjectId = groupSubject.subjectId;
      const isSubjectSelected = selectedSubjectIds.map(String).includes(String(subjectId));
      if (!isSubjectSelected) return;

      const selection = {
        subjectId: subjectId,
        facultyId: null,
        facultyName: '',
        subgroupSelections: []
      };

      if (groupSubject.subGroups && groupSubject.subGroups.length > 0) {
        groupSubject.subGroups.forEach(subgroup => {
          if (selectedSubgroupIds.map(String).includes(String(subgroup.subGroupId))) {
            selection.subgroupSelections.push({
              subGroupId: subgroup.subGroupId,
              facultyId: subgroup.facultyId || null,
              facultyName: subgroup.facultyName || '',
              room: subgroup.room || '',
              capacity: subgroup.capacity || 0
            });
          }
        });
      } else {
        selection.facultyId = groupSubject.facultyId || null;
        selection.facultyName = groupSubject.facultyName || '';
      }
      electiveSelections.push(selection);
    });
    return electiveSelections;
  }, [electiveGroups]);

  const getSubjectName = useCallback((subjectId) => {
    if (!subjectId) return '';
    const subject = subjects.find(s => String(s.id) === String(subjectId) || String(s._id) === String(subjectId));
    return subject?.name || '';
  }, [subjects]);

  // ==================== Template Functions ====================
  
  const fetchTemplates = useCallback(async () => {
    if (!tenantId || !classId || !academicYearId) return;
    
    try {
      setLoadingTemplates(true);
      const params = new URLSearchParams({
        tenantId,
        classId,
        academicYearId,
        type: 'daily_override'
      });
      
      if (isSemesterBased && semesterType) {
        params.append('semesterType', semesterType);
      } else if (termId) {
        params.append('termId', termId);
      }
      
      const response = await api.get(`/api/timetable/templates?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });
      
      if (response.data.success) {
        setTemplates(response.data.data || []);
      }
    } catch (err) {
      console.error('[DailyOverrideModal] Error fetching templates:', err);
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, [tenantId, classId, academicYearId, isSemesterBased, semesterType, termId, token]);
  
  const applyTemplate = useCallback(async (templateId, replaceMode = false) => {
    if (isDateLocked) {
      message.error('Cannot apply template for locked dates');
      return;
    }
    
    if (!templateId) {
      message.warning('Please select a template');
      return;
    }
    
    setApplyingTemplate(true);
    try {
      const response = await api.get(`/api/timetable/templates/${templateId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });
      
      if (response.data.success) {
        const templateEntries = response.data.data.entries || [];
        
        if (templateEntries.length === 0) {
          message.warning('Selected template has no entries');
          return;
        }
        
        let updatedEntries = [...overriddenEntries];
        
        if (replaceMode) {
          // Replace mode: Remove all existing entries
          updatedEntries = [];
        }
        
        // Get existing time slots to avoid duplicates
        const existingTimeKeys = new Set(
          updatedEntries.map(entry => `${entry.timeSlot?.start || ''}-${entry.timeSlot?.end || ''}`)
        );
        
        // Create new entries from template
        const newEntries = [];
        const skippedSlots = [];
        
        templateEntries.forEach(templateEntry => {
          const timeKey = `${templateEntry.timeSlot?.start || ''}-${templateEntry.timeSlot?.end || ''}`;
          
          if (!replaceMode && existingTimeKeys.has(timeKey)) {
            skippedSlots.push(timeKey);
          } else {
            const enhancedEntry = enhanceEntryWithFacultyAndBatchData({
              ...templateEntry,
              dayOfWeek: dayName,
              isModified: true,
              isNew: true
            });
            newEntries.push(enhancedEntry);
          }
        });
        
        updatedEntries = [...updatedEntries, ...newEntries];
        
        // Sort entries by time
        updatedEntries.sort((a, b) => {
          return (a.timeSlot?.start || '').localeCompare(b.timeSlot?.start || '');
        });
        
        setOverriddenEntries(updatedEntries);
        
        // Show appropriate message
        if (newEntries.length > 0) {
          if (skippedSlots.length > 0) {
            message.success(`Added ${newEntries.length} new entries. Skipped ${skippedSlots.length} existing time slots.`);
          } else {
            message.success(`Added ${newEntries.length} entries from template successfully!`);
          }
        } else {
          message.info(`All time slots from this template already exist. No new entries added.`);
        }
        
        setSelectedTemplateId(null);
      } else {
        message.error(response.data.message || 'Failed to load template');
      }
    } catch (err) {
      console.error('[DailyOverrideModal] Error applying template:', err);
      message.error(err.response?.data?.message || 'Error applying template');
    } finally {
      setApplyingTemplate(false);
    }
  }, [token, dayName, enhanceEntryWithFacultyAndBatchData, overriddenEntries, isDateLocked]);

  // ==================== Date Lock Check ====================
  
  useEffect(() => {
    if (selectedDate && academicYearStartDate && academicYearEndDate) {
      const date = moment(selectedDate);
      const start = moment(academicYearStartDate);
      const end = moment(academicYearEndDate);
      
      const isInRange = date.isBetween(start, end, 'day', '[]');
      setIsDateLocked(!isInRange);
      
      if (!isInRange) {
        setError(`Selected date is outside the academic year range (${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')})`);
      }
    }
  }, [selectedDate, academicYearStartDate, academicYearEndDate]);

  // ==================== Load Base Timetable ====================
  
  const loadBaseTimetable = useCallback(async () => {
    // Don't try to load if date is already locked by academic year range
    if (isDateLocked) {
      setLoading(false);
      setOverriddenEntries([]);
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const params = new URLSearchParams({
        tenantId,
        classId,
        academicYearId,
        targetDate: selectedDate
      });

      // Add semester or term based on academic year type
      if (isSemesterBased) {
        params.append('semesterType', semesterType || '');
      } else {
        params.append('termId', termId || '');
      }

      console.log('[DailyOverrideModal] Loading base timetable with params:', params.toString());

      const response = await api.get(`/api/timetable/daily-override/base-for-date?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });

      console.log('[DailyOverrideModal] Load response:', response.data);

      if (response.data.success) {
        const { baseEntries: fetchedBase, dayName: fetchedDay, existingOverride: fetchedOverride } = response.data.data;
        
        const enhancedBaseEntries = fetchedBase.map(entry => 
          enhanceEntryWithFacultyAndBatchData(entry)
        );
        
        setDayName(fetchedDay);
        setExistingOverride(fetchedOverride);
        
        let initialOverrides;
        if (fetchedOverride && fetchedOverride.overriddenEntries) {
          initialOverrides = fetchedOverride.overriddenEntries.map(entry => 
            enhanceEntryWithFacultyAndBatchData(entry)
          );
        } else {
          initialOverrides = enhancedBaseEntries.map(entry => ({
            ...entry,
            isModified: false,
            originalEntryId: entry._id
          }));
        }
        
        setOverriddenEntries(initialOverrides);
      } else {
        // Check if the error is about date being out of range
        if (response.data.message && (
          response.data.message.toLowerCase().includes('outside allowed period') ||
          response.data.message.toLowerCase().includes('not within the academic year') ||
          response.data.message.toLowerCase().includes('out of range')
        )) {
          setIsDateLocked(true);
        }
        setError(response.data.message || 'Failed to load base timetable');
      }
    } catch (err) {
      console.error('[DailyOverrideModal] Error loading base timetable:', err);
      console.log('[DailyOverrideModal] Error response data:', err.response?.data);
      
      // Check for 403 Forbidden error
      if (err.response?.status === 403) {
        const errorMsg = err.response?.data?.message || '';
        
        // Check if it's a date range error
        if (errorMsg.toLowerCase().includes('outside allowed period') ||
            errorMsg.toLowerCase().includes('not within the academic year') ||
            errorMsg.toLowerCase().includes('out of range')) {
          setIsDateLocked(true);
          setError(errorMsg || 'Selected date is outside the allowed academic period');
        } else {
          // Generic 403 error
          setError('You do not have permission to access this timetable');
        }
      } else {
        // Other errors
        const errorMsg = err.response?.data?.message || 'Error loading timetable';
        
        // Check if the error is about date being out of range
        if (errorMsg.toLowerCase().includes('outside allowed period') ||
            errorMsg.toLowerCase().includes('not within the academic year') ||
            errorMsg.toLowerCase().includes('out of range')) {
          setIsDateLocked(true);
        }
        
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  }, [tenantId, classId, academicYearId, isSemesterBased, semesterType, termId, selectedDate, token, enhanceEntryWithFacultyAndBatchData, isDateLocked]);

  // ==================== Effects ====================
  
  useEffect(() => {
    if (visible && tenantId && classId && academicYearId) {
      fetchTemplates();
    }
  }, [visible, tenantId, classId, academicYearId, fetchTemplates]);

  useEffect(() => {
    if (visible && selectedDate && classId && academicYearId && !isDateLocked) {
      loadBaseTimetable();
    } else if (visible && isDateLocked) {
      // If date is locked, clear any existing entries
      setOverriddenEntries([]);
      setDayName('');
      setExistingOverride(null);
    } else {
      setOverriddenEntries([]);
      setDayName('');
      setExistingOverride(null);
      setError('');
      setNewEntryIndex(null);
      setSelectedTemplateId(null);
    }
  }, [visible, selectedDate, classId, academicYearId, isDateLocked, loadBaseTimetable]);

  useEffect(() => {
    if (newEntryIndex !== null && entriesEndRef.current) {
      entriesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      setNewEntryIndex(null);
    }
  }, [newEntryIndex, overriddenEntries.length]);

  // ==================== Entry Handlers ====================
  
  const handleEntryUpdate = useCallback((index, field, value) => {
    if (isDateLocked) return; // Prevent updates if date is locked
    
    setOverriddenEntries(prevEntries => {
      const updated = [...prevEntries];
      
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        if (!updated[index][parent]) updated[index][parent] = {};
        updated[index][parent][child] = value;
      } else {
        updated[index][field] = value;
      }
      
      if (field === 'facultyUserId' && value) {
        const facultyInfo = facultyMap()[value];
        if (facultyInfo) updated[index].facultyName = facultyInfo.name;
      }
      
      if (field === 'batchId' && value) {
        const batchInfo = formattedLabBatches().find(batch => 
          String(batch._id) === String(value) || String(batch.id) === String(value)
        );
        if (batchInfo) updated[index].batchName = batchInfo.name || batchInfo.batchName;
      }
      
      if (field === 'room') {
        const currentAllocations = Array.isArray(updated[index].allocations) ? [...updated[index].allocations] : [];
        if (currentAllocations.length) {
          updated[index].allocations = currentAllocations.map(a => ({ 
            ...(a || {}), 
            room: (a && a.room) ? a.room : value || '' 
          }));
        }
      }
      
      if (!updated[index].isNew) updated[index].isModified = true;
      return updated;
    });
  }, [facultyMap, formattedLabBatches, isDateLocked]);

  const handleEntryRemove = useCallback((index) => {
    if (isDateLocked) return; // Prevent removal if date is locked
    setOverriddenEntries(prevEntries => prevEntries.filter((_, i) => i !== index));
  }, [isDateLocked]);

  const handleAddNewEntry = useCallback(() => {
    if (isDateLocked) return; // Prevent adding if date is locked
    
    const facultyMapData = facultyMap();
    const defaultFaculty = Object.values(facultyMapData)[0];
    const defaultLabBatch = (formattedLabBatches() || [])[0];

    const newEntry = {
      dayOfWeek: dayName,
      timeSlot: { start: '09:00', end: '10:00' },
      type: 'Period',
      label: '',
      room: '',
      batchId: defaultLabBatch ? String(defaultLabBatch._id || defaultLabBatch.id || defaultLabBatch.batchId) : null,
      batchName: defaultLabBatch ? (defaultLabBatch.name || defaultLabBatch.batchName) : '',
      facultyName: defaultFaculty?.name || '',
      facultyUserId: defaultFaculty?.id || null,
      subjectId: null,
      subjectName: '',
      subjectCode: '',
      isModified: true,
      isNew: true,
      allocations: [],
      electiveGroupId: null,
      electiveSelections: [],
      electiveSubjectId: null,
      electiveSubjectIds: null,
      electiveSubGroupIds: null,
      conflictApprovals: []
    };
    
    setOverriddenEntries(prevEntries => {
      const newEntries = [...prevEntries, newEntry];
      setNewEntryIndex(newEntries.length - 1);
      return newEntries;
    });
  }, [dayName, facultyMap, formattedLabBatches, isDateLocked]);

  const handleSubjectChange = useCallback(async (index, subjectId) => {
    if (isDateLocked) return; // Prevent changes if date is locked
    
    const subject = subjects.find(s => 
      String(s.id) === String(subjectId) || String(s._id) === String(subjectId)
    );
    
    if (subject) {
      setOverriddenEntries(prevEntries => {
        const updated = [...prevEntries];
        updated[index].subjectId = subjectId;
        updated[index].subjectName = subject.name;
        updated[index].subjectCode = subject.subjectCode;
        updated[index].isModified = true;
        
        let derivedFacultyId = null;
        let derivedFacultyName = '';
        const af = subject.assignedFaculty || subject.assigned_faculty || subject.facultyAssigned || [];
        if (Array.isArray(af) && af.length) {
          const f = af[0];
          derivedFacultyId = f?.userId || f?.facultyId || f?._id || null;
          const first = f?.firstName || f?.name || (f?.user && (f.user.firstName || f.user.name)) || f?.fullName || '';
          const last = f?.lastName || f?.surname || (f?.user && f.user.lastName) || '';
          derivedFacultyName = [first, last].filter(Boolean).join(' ').trim();
        }

        if (derivedFacultyId) {
          updated[index].facultyUserId = derivedFacultyId;
          if (derivedFacultyName) updated[index].facultyName = derivedFacultyName;
        }

        if (!updated[index].allocations || updated[index].allocations.length === 0) {
          updated[index].allocations = [{
            subjectId: subjectId,
            facultyUserId: updated[index].facultyUserId || null,
            batchId: updated[index].batchId || null,
            room: updated[index].room || '',
            label: updated[index].label || ''
          }];
        } else {
          updated[index].allocations = updated[index].allocations.map(a => ({
            ...a,
            subjectId: a.subjectId || subjectId,
            facultyUserId: a.facultyUserId || updated[index].facultyUserId || null,
            batchId: a.batchId || updated[index].batchId || null,
            room: a.room || updated[index].room || ''
          }));
        }
        return updated;
      });
    } else {
      handleEntryUpdate(index, 'subjectId', subjectId);
    }
  }, [subjects, handleEntryUpdate, isDateLocked]);

  // ==================== Save/Delete Override ====================
  
  const cleanEntryForApi = useCallback((entry) => {
    const cleaned = { ...entry };
    const frontendFields = [
      'isNew', 'isModified', 'originalEntryId', 'subjectName',
      'subjectCode', 'facultyEmail', 'batchName', 'electiveGroupName',
      '_id', '__v', 'createdAt', 'updatedAt'
    ];
    frontendFields.forEach(f => delete cleaned[f]);

    // Convert empty strings to null for ObjectId fields
    ['batchId', 'subjectId', 'facultyUserId', 'electiveGroupId', 'electiveSubjectId'].forEach(k => {
      if (cleaned[k] === '') cleaned[k] = null;
    });

    if (cleaned.timeSlot) {
      cleaned.timeSlot = {
        start: cleaned.timeSlot.start || null,
        end: cleaned.timeSlot.end || null
      };
    }

    if (Array.isArray(cleaned.allocations)) {
      cleaned.allocations = cleaned.allocations.map(a => {
        const alloc = { ...a };
        delete alloc.subjectName;
        delete alloc.facultyName;
        delete alloc.batchName;
        if (alloc.subjectId === '') alloc.subjectId = null;
        if (alloc.batchId === '') alloc.batchId = null;
        if (alloc.facultyUserId === '') alloc.facultyUserId = null;
        if (!alloc.subjectId && cleaned.subjectId) alloc.subjectId = cleaned.subjectId;
        return alloc;
      }).filter(a => Boolean(a.subjectId));
    }

    if (cleaned.electiveGroupId) {
      const subIds = Array.isArray(cleaned.electiveSubjectIds) ? cleaned.electiveSubjectIds : [];
      const subGIds = Array.isArray(cleaned.electiveSubGroupIds) ? cleaned.electiveSubGroupIds : [];
      cleaned.electiveSelections = buildElectiveSelections(cleaned.electiveGroupId, subIds, subGIds);
    }

    if (cleaned.type !== 'Lab') {
      cleaned.batchId = null;
    }

    return cleaned;
  }, [buildElectiveSelections]);

  const handleSaveOverride = async () => {
    if (isDateLocked) {
      setError('Cannot save override for dates outside the academic year');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setConflicts([]);

      if (!tenantId || !classId || !academicYearId || !selectedDate) {
        setError('Missing required identification fields');
        return;
      }

      const payload = {
        tenantId: tenantId.trim(),
        organizationId: organizationId || '',
        classId: classId.trim(),
        className: className || '',
        academicYearId: academicYearId.trim(),
        academicYearName: academicYearName || '',
        targetDate: selectedDate,
        overriddenEntries: overriddenEntries.map(cleanEntryForApi),
        createdBy: createdBy || null,
        createdByName: createdByName || '',
        updatedBy: createdBy || null,
        updatedByName: createdByName || ''
      };

      // Add semester or term based on academic year type
      if (isSemesterBased) {
        payload.semesterType = semesterType ? semesterType.trim() : null;
      } else {
        payload.termId = termId ? termId.trim() : null;
        payload.termNumber = termNumber || null;
        payload.termName = termName || null;
      }

      console.log('[DailyOverrideModal] Saving override with payload:', payload);

      const response = await api.post('/api/timetable/daily-override/save', payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });

      console.log('[DailyOverrideModal] Save response:', response.data);

      if (response.data.success) {
        message.success('Override saved successfully');
        onSave(response.data.data);
      } else {
        setError(response.data.message || 'Failed to save override');
        setConflicts(response.data.conflicts || []);
      }
    } catch (err) {
      console.error('[DailyOverrideModal] Error saving override:', err);
      if (err.response?.status === 409 && err.response?.data?.conflicts) {
        setError(err.response.data.message || 'Faculty conflict detected');
        setConflicts(err.response.data.conflicts);
      } else {
        setError(err.response?.data?.message || 'Error saving override');
        setConflicts([]);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOverride = async () => {
    if (isDateLocked) {
      setError('Cannot delete override for dates outside the academic year');
      return;
    }

    if (!existingOverride) return;
    try {
      setSaving(true);
      
      const payload = {
        tenantId,
        classId,
        academicYearId,
        targetDate: selectedDate,
        deletedBy: createdBy || null
      };

      if (isSemesterBased) {
        payload.semesterType = semesterType;
      } else {
        payload.termId = termId;
      }

      const response = await api.delete(`/api/timetable/daily-override/${existingOverride._id}`, {
        data: payload,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });
      
      if (response.data.success) {
        message.success('Override deleted successfully');
        onSave(null);
        setOverriddenEntries([]);
        setExistingOverride(null);
      }
    } catch (err) {
      console.error('[DailyOverrideModal] Error deleting override:', err);
      setError(err.response?.data?.message || 'Error deleting override');
    } finally {
      setSaving(false);
    }
  };

  // ==================== Stats & UI Helpers ====================
  
  const stats = {
    activeEntries: overriddenEntries.length,
    modifiedCount: overriddenEntries.filter(e => e.isModified).length,
    newCount: overriddenEntries.filter(e => e.isNew).length
  };

  const getStatusBadge = () => {
    if (isDateLocked) {
      return <Badge status="warning" text="Date Locked - View Only" />;
    }
    if (existingOverride) {
      return <Badge status="success" text="Override Active" />;
    }
    return <Badge status="processing" text="Editing Mode" />;
  };

  const isOutOfRange = isDateLocked || (error && (
    error.toLowerCase().includes('outside allowed period') ||
    error.toLowerCase().includes('not within the academic year') ||
    error.toLowerCase().includes('out of range') ||
    error.toLowerCase().includes('forbidden') ||
    error.toLowerCase().includes('permission')
  ));

  const templateMenu = (
    <Menu>
      {loadingTemplates ? (
        <Menu.Item key="loading" disabled>
          <Spin size="small" /> Loading templates...
        </Menu.Item>
      ) : templates.length === 0 ? (
        <Menu.Item key="empty" disabled>
          No templates available
        </Menu.Item>
      ) : (
        <>
          <Menu.Item key="add" onClick={() => applyTemplate(selectedTemplateId, false)} disabled={!selectedTemplateId || applyingTemplate}>
            <div className="flex items-center gap-2">
              <PlusOutlined className="text-green-600" />
              <span>Add Missing Slots Only</span>
              <span className="text-xs text-gray-500">(Preserve existing)</span>
            </div>
          </Menu.Item>
          <Menu.Item key="replace" onClick={() => applyTemplate(selectedTemplateId, true)} disabled={!selectedTemplateId || applyingTemplate}>
            <div className="flex items-center gap-2">
              <DeleteOutlined className="text-orange-600" />
              <span>Replace All Slots</span>
              <span className="text-xs text-gray-500">(Remove existing)</span>
            </div>
          </Menu.Item>
          <Menu.Divider />
          <Menu.SubMenu
            key="templates"
            title={
              <div className="flex items-center gap-2">
                <ClockCircleOutlined />
                <span>Select Template</span>
              </div>
            }
          >
            {templates.map(template => (
              <Menu.Item 
                key={template._id} 
                onClick={() => setSelectedTemplateId(template._id)}
                style={selectedTemplateId === template._id ? { background: '#e6f7ff' } : {}}
              >
                <div className="flex justify-between items-center w-full">
                  <span>{template.name}</span>
                  <Badge count={template.entries?.length || 0} className="ml-2" />
                </div>
              </Menu.Item>
            ))}
          </Menu.SubMenu>
        </>
      )}
    </Menu>
  );

  // ==================== Render ====================
  
  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onCancel}
      width={1300}
      style={{ top: 20 }}
      footer={null}
      className="daily-override-modal"
      closeIcon={<CloseOutlined className="text-gray-400 hover:text-gray-600" />}
    >
      {/* Custom Header */}
      <div className="modal-header" style={{
        padding: '24px 24px 16px',
        borderBottom: '1px solid #f0f0f0',
        background: isDateLocked 
          ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '12px 12px 0 0',
        margin: '-20px -20px 0',
      }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
              {isDateLocked ? (
                <LockOutlined className="text-white text-2xl" />
              ) : (
                <EditOutlined className="text-white text-2xl" />
              )}
            </div>
            <div>
              <Title level={4} className="!text-white !mb-1 !font-bold">
                {isDateLocked ? 'View Only - Date Locked' : 'Daily Timetable Override'}
              </Title>
              <div className="flex items-center gap-3 text-white/90">
                <Space>
                  <CalendarOutlined />
                  <Text className="!text-white/90 text-sm">
                    {selectedDate ? moment(selectedDate).format('dddd, MMMM D, YYYY') : 'Select a date'}
                  </Text>
                </Space>
                {termName && !isSemesterBased && (
                  <>
                    <span className="text-white/50">•</span>
                    <Tag color="gold" className="!bg-white/20 !border-0 !text-white">
                      {termName}
                    </Tag>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge()}
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '24px' }}>
        {/* Warning for locked date */}
        {isOutOfRange && (
          <Alert
            icon={<LockOutlined />}
            message="Date Outside Academic Period"
            description={
              academicYearStartDate && academicYearEndDate
                ? `The selected date (${moment(selectedDate).format('MMMM D, YYYY')}) is outside the allowed academic period (${moment(academicYearStartDate).format('MMM D, YYYY')} - ${moment(academicYearEndDate).format('MMM D, YYYY')}). You can view existing overrides but cannot create, modify, or delete entries for this date.`
                : "The selected date is outside the allowed academic period. You can view existing overrides but cannot create, modify, or delete entries for this date."
            }
            type="warning"
            showIcon
            className="mb-6 rounded-xl border-0 shadow-sm"
            style={{ background: '#fff7e6', border: '1px solid #ffe7ba' }}
          />
        )}


        {error && !isOutOfRange && (
          <>
            <Alert 
              message={error} 
              type="error" 
              showIcon 
              className="mb-4 rounded-xl" 
              closable 
              onClose={() => setError('')} 
            />
            {conflicts.length > 0 && (
              <List
                className="mb-6"
                header={<b>Conflicting Sessions</b>}
                bordered
                dataSource={conflicts}
                renderItem={item => (
                  <List.Item>
                    <div>
                      <b>Faculty:</b> {item.facultyId || 'Unknown'}<br/>
                      <b>Message:</b> {item.message}<br/>
                      {item.entries && item.entries.length === 2 && (
                        <>
                          <b>Session 1:</b> {item.entries[0]?.dayOfWeek}, {item.entries[0]?.timeSlot?.start} - {item.entries[0]?.timeSlot?.end}<br/>
                          <b>Session 2:</b> {item.entries[1]?.dayOfWeek}, {item.entries[1]?.timeSlot?.start} - {item.entries[1]?.timeSlot?.end}
                        </>
                      )}
                      {item.newEntry && item.existingEntry && (
                        <>
                          <b>New Session:</b> {item.newEntry?.dayOfWeek}, {item.newEntry?.timeSlot?.start} - {item.newEntry?.timeSlot?.end}<br/>
                          <b>Existing Session:</b> {item.existingEntry?.dayOfWeek}, {item.existingEntry?.timeSlot?.start} - {item.existingEntry?.timeSlot?.end} ({item.existingEntry?.className || ''})
                        </>
                      )}
                    </div>
                  </List.Item>
                )}
              />
            )}
          </>
        )}

        {loading ? (
          <div className="text-center py-16">
            <Spin size="large" tip="Loading timetable data..." />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Stats Cards - Only show if not locked or if there are entries */}
            {(!isOutOfRange || overriddenEntries.length > 0) && (
              <div className="grid grid-cols-4 gap-4">
                <Card className={`rounded-xl border-0 shadow-sm ${isOutOfRange ? 'bg-gray-50' : 'bg-gradient-to-br from-blue-50 to-indigo-50'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <Text type="secondary" className="text-xs uppercase font-bold">Total Entries</Text>
                      <div className={`text-2xl font-bold ${isOutOfRange ? 'text-gray-600' : 'text-blue-600'}`}>
                        {stats.activeEntries}
                      </div>
                    </div>
                    <div className={`p-3 rounded-xl ${isOutOfRange ? 'bg-gray-200' : 'bg-blue-100'}`}>
                      <BookOutlined className={isOutOfRange ? 'text-gray-600 text-xl' : 'text-blue-600 text-xl'} />
                    </div>
                  </div>
                </Card>

                <Card className={`rounded-xl border-0 shadow-sm ${isOutOfRange ? 'bg-gray-50' : 'bg-gradient-to-br from-green-50 to-emerald-50'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <Text type="secondary" className="text-xs uppercase font-bold">Modified</Text>
                      <div className={`text-2xl font-bold ${isOutOfRange ? 'text-gray-600' : 'text-green-600'}`}>
                        {stats.modifiedCount}
                      </div>
                    </div>
                    <div className={`p-3 rounded-xl ${isOutOfRange ? 'bg-gray-200' : 'bg-green-100'}`}>
                      <EditOutlined className={isOutOfRange ? 'text-gray-600 text-xl' : 'text-green-600 text-xl'} />
                    </div>
                  </div>
                </Card>

                <Card className={`rounded-xl border-0 shadow-sm ${isOutOfRange ? 'bg-gray-50' : 'bg-gradient-to-br from-purple-50 to-pink-50'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <Text type="secondary" className="text-xs uppercase font-bold">New</Text>
                      <div className={`text-2xl font-bold ${isOutOfRange ? 'text-gray-600' : 'text-purple-600'}`}>
                        {stats.newCount}
                      </div>
                    </div>
                    <div className={`p-3 rounded-xl ${isOutOfRange ? 'bg-gray-200' : 'bg-purple-100'}`}>
                      <PlusOutlined className={isOutOfRange ? 'text-gray-600 text-xl' : 'text-purple-600 text-xl'} />
                    </div>
                  </div>
                </Card>

                <Card className={`rounded-xl border-0 shadow-sm ${isOutOfRange ? 'bg-gray-50' : 'bg-gradient-to-br from-orange-50 to-amber-50'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <Text type="secondary" className="text-xs uppercase font-bold">Day</Text>
                      <div className={`text-2xl font-bold ${isOutOfRange ? 'text-gray-600' : 'text-orange-600'}`}>
                        {dayName || 'N/A'}
                      </div>
                    </div>
                    <div className={`p-3 rounded-xl ${isOutOfRange ? 'bg-gray-200' : 'bg-orange-100'}`}>
                      <CalendarOutlined className={isOutOfRange ? 'text-gray-600 text-xl' : 'text-orange-600 text-xl'} />
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {/* Action Bar */}
            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex items-center gap-4">
                {isOutOfRange ? (
                  <LockOutlined className="text-gray-400" />
                ) : (
                  <CheckCircleOutlined className="text-green-500" />
                )}
                <div>
                  <Text strong className="text-sm block">
                    {isOutOfRange 
                      ? 'View Only Mode' 
                      : existingOverride 
                        ? 'Existing override loaded' 
                        : 'Base timetable loaded'}
                  </Text>
                  <Text type="secondary" className="text-xs">
                    {isOutOfRange 
                      ? 'Viewing existing overrides only - modifications disabled' 
                      : 'Modify entries below. Changes apply only to this specific date.'}
                  </Text>
                </div>
              </div>
              <div className="flex gap-2">
                {!isOutOfRange && (
                  <>
                    <Dropdown overlay={templateMenu} trigger={['click']} placement="bottomRight">
                      <Button 
                        icon={<ClockCircleOutlined />}
                        className="rounded-xl h-10 px-4 border-gray-200"
                        loading={loadingTemplates || applyingTemplate}
                      >
                        Apply Template {selectedTemplateId && templates.find(t => t._id === selectedTemplateId) && `(${templates.find(t => t._id === selectedTemplateId)?.name})`}
                      </Button>
                    </Dropdown>
                    
                    <Button 
                      type="primary" 
                      icon={<PlusOutlined />} 
                      onClick={handleAddNewEntry}
                      className="rounded-xl h-10 px-6 font-semibold shadow-lg shadow-blue-200"
                      style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}
                    >
                      Add New Entry
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Entries Grid */}
            <div className="max-h-96 overflow-y-auto px-1">
              {overriddenEntries.length === 0 ? (
                <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50">
                  {!isOutOfRange ? (
                    <>
                      <PlusOutlined className="text-4xl text-gray-300 mb-3" />
                      <Text className="block text-gray-500 mb-3">No entries found for this date</Text>
                      <div className="flex gap-3 justify-center">
                        <Button 
                          type="primary" 
                          icon={<PlusOutlined />} 
                          onClick={handleAddNewEntry}
                          className="rounded-xl"
                          style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}
                        >
                          Create First Entry
                        </Button>
                        {templates.length > 0 && (
                          <Dropdown overlay={templateMenu} trigger={['click']}>
                            <Button 
                              icon={<ClockCircleOutlined />}
                              className="rounded-xl"
                            >
                              Apply Template
                            </Button>
                          </Dropdown>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <LockOutlined className="text-4xl text-gray-300 mb-3" />
                      <Text className="block text-gray-500">No entries available for this date</Text>
                      {academicYearStartDate && academicYearEndDate && (
                        <Text type="secondary" className="text-xs block mt-2">
                          Academic Year: {moment(academicYearStartDate).format('MMM D, YYYY')} - {moment(academicYearEndDate).format('MMM D, YYYY')}
                        </Text>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {overriddenEntries.map((entry, index) => (
                    <div key={index} className="relative group" ref={index === overriddenEntries.length - 1 ? entriesEndRef : null}>
                      {/* Status Badges */}
                      <div className="absolute -top-2 -right-2 z-10 flex gap-1">
                        {entry.isNew && !isOutOfRange && (
                          <Tag color="green" className="!rounded-full !px-3 !py-0.5 !border-0 shadow-lg">
                            NEW
                          </Tag>
                        )}
                        {entry.isModified && !entry.isNew && !isOutOfRange && (
                          <Tag color="blue" className="!rounded-full !px-3 !py-0.5 !border-0 shadow-lg">
                            MODIFIED
                          </Tag>
                        )}
                      </div>

                      {/* Entry Card */}
                      <Card 
                        className={`rounded-xl border-2 transition-all hover:shadow-lg ${
                          isOutOfRange
                            ? 'border-gray-200 bg-gray-50'
                            : entry.isNew 
                              ? 'border-green-200 bg-green-50/30' 
                              : entry.isModified 
                                ? 'border-blue-200 bg-blue-50/30'
                                : 'border-gray-100 hover:border-gray-200'
                        }`}
                        bodyStyle={{ padding: '16px' }}
                      >
                        <TimetableEntry
                          entry={entry}
                          index={index}
                          subjects={subjects}
                          labBatches={formattedLabBatches()}
                          facultyData={facultyData}
                          onUpdate={handleEntryUpdate}
                          onRemove={handleEntryRemove}
                          onSubjectChange={handleSubjectChange}
                          getSubjectName={getSubjectName}
                          saveTimetable={() => {}}
                          tenantId={tenantId}
                          selectedAcademicYearId={academicYearId}
                          classId={classId}
                          isSemesterBased={isSemesterBased}
                          isOverrideMode={true}
                          electiveGroups={electiveGroups}
                          loadingElectiveGroups={loadingElectives}
                          token={token}
                          disableEditing={isOutOfRange}
                        />
                      </Card>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ 
        padding: '16px 24px', 
        borderTop: '1px solid #f0f0f0',
        background: '#fafafa',
        borderRadius: '0 0 12px 12px',
        margin: '0 -20px -20px'
      }}>
        <div className="flex justify-end gap-3">
          <Button 
            key="cancel" 
            onClick={onCancel} 
            disabled={saving}
            className="rounded-xl h-10 px-6 border-gray-200 hover:border-gray-300"
          >
            <CloseOutlined /> Cancel
          </Button>
          
          {existingOverride && !isOutOfRange && (
            <Button 
              key="delete" 
              danger 
              onClick={handleDeleteOverride} 
              loading={saving}
              className="rounded-xl h-10 px-6"
              icon={<DeleteOutlined />}
            >
              Delete Override
            </Button>
          )}
          
          <Button 
            key="save" 
            type="primary" 
            onClick={handleSaveOverride}
            loading={saving}
            icon={<SaveOutlined />}
            disabled={saving || overriddenEntries.length === 0 || isOutOfRange}
            className="rounded-xl h-10 px-8 font-semibold shadow-lg"
            style={{ 
              background: !isOutOfRange ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#d9d9d9',
              border: 'none',
              opacity: isOutOfRange ? 0.5 : 1
            }}
          >
            {existingOverride ? 'Update Override' : 'Save Override'}
          </Button>
        </div>
      </div>

      <style jsx global>{`
        .daily-override-modal .ant-modal-content {
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
        }
        
        .daily-override-modal .ant-modal-body {
          padding: 0;
        }
        
        .daily-override-modal .ant-card-body {
          padding: 16px;
        }
        
        /* Custom scrollbar */
        .overflow-y-auto::-webkit-scrollbar {
          width: 6px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 10px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </Modal>
  );
};

export default DailyOverrideModal;